import React from "react";
import HomeNavSection from "./sections/HomeNavSection";
import HomeMarqueeSection from "./sections/HomeMarqueeSection";
import HomeCtaSection from "./sections/HomeCtaSection";
import HomeFooterSection from "./sections/HomeFooterSection";

export default function HomeTemplate() {
  return (
    <div>
      {/* ═══════════════════ NAV ═══════════════════ */}
      <HomeNavSection />
      {/* ═══════════════════ HERO ═══════════════════ */}
      <section className="relative overflow-hidden bg-background-light pt-14 pb-0 px-6 lg:px-20">
        {/* Dot pattern bg */}
        <div className="absolute inset-0 hero-pattern opacity-[0.06] pointer-events-none" />
        {/* Ambient gradient orb */}
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-primary/12 to-orange-100/40 blur-3xl pointer-events-none orb-drift" />
        <div
          className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-amber-100/50 to-transparent blur-3xl pointer-events-none orb-drift"
          style={{ animationDelay: "-6s" }}
        />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
            {/* ── Copy ── */}
            <div className="pb-20 lg:pb-28">
              <div className="hero-line-1 inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-xs font-black tracking-widest uppercase mb-8">
                <span className="w-2 h-2 bg-primary rounded-full status-blink" />
                On-demand errand service
              </div>
              <h1 className="hero-line-2 text-5xl md:text-6xl lg:text-[4.3rem] font-black leading-[1.05] tracking-tight mb-7">
                We send a<br />
                <span className="text-primary italic skew-heading relative">
                  trusted runner
                  <svg
                    className="absolute -bottom-2 left-0 w-full"
                    height={6}
                    viewBox="0 0 400 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 4 Q100 0 200 4 Q300 8 400 4"
                      stroke="#ff6933"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      fill="none"
                      className="draw-path-anim"
                      strokeDasharray={600}
                      strokeDashoffset={600}
                    />
                  </svg>
                </span>
                <br />
                for your errands.
              </h1>
              <p className="hero-line-3 text-slate-500 text-[1.05rem] font-medium leading-relaxed max-w-[480px] mb-10">
                Errandi connects you with verified local runners who handle your
                everyday errands — pickups, drop-offs, deliveries, and more. You
                describe the task, choose your runner, pay securely, and track
                every step live until it's done.
              </p>
              <div className="hero-line-4 flex flex-wrap gap-4 mb-12">
                <button className="bg-primary text-white font-extrabold px-9 py-4 rounded-2xl text-base hover:shadow-2xl hover:shadow-primary/30 hover:scale-[1.04] transition-all active:scale-95 flex items-center gap-2.5 relative overflow-hidden group">
                  <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
                  <span
                    className="material-symbols-outlined relative z-10"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    directions_run
                  </span>
                  <span className="relative z-10">Send a Runner</span>
                </button>
                <button className="border-2 border-slate-200 text-slate-700 font-bold px-7 py-4 rounded-2xl text-base hover:border-primary hover:text-primary transition-all">
                  Watch how it works ↓
                </button>
              </div>
              <div className="hero-line-5 flex flex-wrap gap-x-8 gap-y-3">
                <div className="flex items-center gap-2 text-sm font-bold text-slate-500 group cursor-default">
                  <span
                    className="material-symbols-outlined text-primary text-base group-hover:scale-110 transition-transform"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    verified
                  </span>
                  Verified runners only
                </div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-500 group cursor-default">
                  <span
                    className="material-symbols-outlined text-primary text-base group-hover:scale-110 transition-transform"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    lock
                  </span>
                  Escrow-protected payments
                </div>
                <div className="flex items-center gap-2 text-sm font-bold text-slate-500 group cursor-default">
                  <span
                    className="material-symbols-outlined text-primary text-base group-hover:scale-110 transition-transform"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    location_on
                  </span>
                  Live GPS tracking
                </div>
              </div>
            </div>
            {/* ── HERO ILLUSTRATION ── */}
            <div className="panel-rise relative h-[540px] lg:h-[620px] rounded-t-[2.5rem] overflow-hidden bg-gradient-to-br from-orange-50/80 via-amber-50/60 to-primary/5 flex items-center justify-center border border-primary/8 border-b-0 shadow-[0_-20px_80px_rgba(255,105,51,0.08)]">
              {/* City grid bg */}
              <div className="absolute inset-0 map-grid opacity-50" />
              {/* Road SVG */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 460 560"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMidYMid slice"
              >
                {/* horizontal road */}
                <rect
                  x={0}
                  y={310}
                  width={460}
                  height={24}
                  rx={4}
                  fill="#ebe6e2"
                  opacity="0.9"
                />
                <line
                  x1={0}
                  y1={322}
                  x2={460}
                  y2={322}
                  stroke="white"
                  strokeWidth="2.5"
                  strokeDasharray="26 16"
                  className="road-dash"
                />
                {/* vertical road */}
                <rect
                  x={218}
                  y={0}
                  width={24}
                  height={560}
                  rx={4}
                  fill="#ebe6e2"
                  opacity="0.9"
                />
                <line
                  x1={230}
                  y1={0}
                  x2={230}
                  y2={560}
                  stroke="white"
                  strokeWidth="2.5"
                  strokeDasharray="26 16"
                  className="road-dash"
                />
                {/* block fills */}
                <rect
                  x={12}
                  y={14}
                  width={190}
                  height={280}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.35"
                />
                <rect
                  x={258}
                  y={14}
                  width={188}
                  height={280}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.3"
                />
                <rect
                  x={12}
                  y={350}
                  width={190}
                  height={192}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.35"
                />
                <rect
                  x={258}
                  y={350}
                  width={188}
                  height={192}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.3"
                />
                {/* building sketches */}
                <rect
                  x={32}
                  y={40}
                  width={22}
                  height={55}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.5"
                />
                <rect
                  x={62}
                  y={60}
                  width={18}
                  height={35}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.4"
                />
                <rect
                  x={100}
                  y={30}
                  width={26}
                  height={70}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.45"
                />
                <rect
                  x={280}
                  y={35}
                  width={24}
                  height={65}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.5"
                />
                <rect
                  x={316}
                  y={55}
                  width={20}
                  height={45}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.4"
                />
                <rect
                  x={360}
                  y={28}
                  width={18}
                  height={72}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.45"
                />
                <rect
                  x={32}
                  y={376}
                  width={20}
                  height={50}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.4"
                />
                <rect
                  x={280}
                  y={370}
                  width={24}
                  height={58}
                  rx={3}
                  fill="#cdc8c2"
                  opacity="0.4"
                />
              </svg>
              {/* Buildings silhouette */}
              <div className="absolute bottom-0 left-4 flex items-end gap-1.5 opacity-20">
                <div className="w-9  h-20 bg-slate-500 rounded-t" />
                <div className="w-7  h-32 bg-slate-600 rounded-t" />
                <div className="w-11 h-16 bg-slate-500 rounded-t" />
                <div className="w-5  h-28 bg-slate-600 rounded-t" />
              </div>
              <div className="absolute bottom-0 right-3 flex items-end gap-1.5 opacity-15">
                <div className="w-7  h-28 bg-slate-600 rounded-t" />
                <div className="w-10 h-18 bg-slate-500 rounded-t" />
                <div className="w-5  h-36 bg-slate-600 rounded-t" />
              </div>
              {/* Centre runner with concentric pulse rings */}
              <div className="relative z-20 flex flex-col items-center gap-0">
                <div className="relative mb-3">
                  {/* Outer pulse rings */}
                  <div
                    className="absolute inset-0 m-auto rounded-full bg-primary/15 ring-pulse-a"
                    style={{ width: 96, height: 96, top: 0, left: 0 }}
                  />
                  <div
                    className="absolute inset-0 m-auto rounded-full bg-primary/10 ring-pulse-b"
                    style={{ width: 96, height: 96, top: 0, left: 0 }}
                  />
                  {/* Main circle */}
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary-deep flex items-center justify-center shadow-2xl shadow-primary/40 relative z-10">
                    <span
                      className="material-symbols-outlined text-white runner-anim"
                      style={{
                        fontSize: "2.8rem",
                        fontVariationSettings: '"FILL" 1',
                      }}
                    >
                      directions_run
                    </span>
                  </div>
                </div>
                <div className="bg-white rounded-full px-4 py-1.5 shadow-xl text-xs font-black text-primary tracking-wider uppercase border border-primary/10 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full status-blink" />
                  Runner Active
                </div>
              </div>
              {/* Orbiting delivery icons */}
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                style={{ marginTop: "-20px" }}
              >
                <div className="relative w-0 h-0">
                  <div
                    className="orbit-1 absolute w-11 h-11 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/80"
                    style={{ top: "-11px", left: "-11px" }}
                  >
                    <span
                      className="material-symbols-outlined text-primary text-xl"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      inventory_2
                    </span>
                  </div>
                  <div
                    className="orbit-2 absolute w-11 h-11 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/80"
                    style={{ top: "-11px", left: "-11px" }}
                  >
                    <span
                      className="material-symbols-outlined text-primary text-xl"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      shopping_bag
                    </span>
                  </div>
                  <div
                    className="orbit-3 absolute w-11 h-11 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/80"
                    style={{ top: "-11px", left: "-11px" }}
                  >
                    <span
                      className="material-symbols-outlined text-primary text-xl"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      local_pharmacy
                    </span>
                  </div>
                </div>
              </div>
              {/* Destination pin top-right */}
              <div className="absolute top-10 right-10 z-20 pin-1">
                <div className="bg-primary text-white rounded-2xl p-2.5 shadow-2xl border-4 border-white">
                  <span
                    className="material-symbols-outlined text-2xl"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    location_on
                  </span>
                </div>
              </div>
              {/* User pin top-left */}
              <div className="absolute top-20 left-12 z-20 pin-2">
                <div className="bg-white text-primary rounded-2xl p-2 shadow-xl border-2 border-primary/15">
                  <span
                    className="material-symbols-outlined text-xl"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    person_pin_circle
                  </span>
                </div>
              </div>
              {/* Small pin bottom-left */}
              <div className="absolute bottom-36 left-10 z-20 pin-3">
                <div className="bg-white rounded-full p-1.5 shadow-lg border border-primary/15 glow-dot">
                  <div className="w-4 h-4 bg-blue-500 rounded-full relative">
                    <div className="absolute inset-0 bg-blue-400 rounded-full live-ping" />
                  </div>
                </div>
              </div>
              {/* Bottom fade */}
              <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-background-light via-background-light/60 to-transparent pointer-events-none" />
              {/* Notification cards — staggered slide-in */}
              <div className="notif-1 absolute bottom-12 left-5 bg-white rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3 z-30 border border-slate-100/80">
                <div className="bg-primary/10 p-2 rounded-xl flex-shrink-0">
                  <span
                    className="material-symbols-outlined text-primary text-lg"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    directions_run
                  </span>
                </div>
                <div>
                  <div className="text-[11px] text-slate-400 font-semibold">
                    Runner en route
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    Arriving in <span className="text-primary">8 min</span>
                  </div>
                </div>
                <div className="ml-1 flex items-center gap-1.5 pl-3 border-l border-slate-100">
                  <div className="w-2 h-2 bg-green-500 rounded-full status-blink" />
                  <span className="text-[11px] text-green-600 font-black">
                    Live
                  </span>
                </div>
              </div>
              <div className="notif-2 absolute top-10 right-5 bg-white rounded-2xl px-4 py-3 shadow-2xl z-30 border border-slate-100/80">
                <div className="text-[11px] text-slate-400 font-semibold mb-1.5">
                  300+ runners active
                </div>
                <div className="flex items-center gap-1.5 text-primary font-black text-lg">
                  <span
                    className="material-symbols-outlined text-xl star-t1"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span className="gradient-text">4.9</span>
                  <span className="text-slate-400 font-normal text-xs ml-0.5">
                    avg. rating
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════ MARQUEE ═══════════════════ */}
      <HomeMarqueeSection />
      {/* ═══════════════════ HOW IT WORKS ═══════════════════ */}
      <section id="how" className="py-28 px-6 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 fade-up">
            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase mb-5">
              <span className="material-symbols-outlined text-sm">route</span>
              The Process
            </div>
            <h2 className="text-4xl font-black text-slate-900 skew-heading mb-4">
              How it works
            </h2>
            <p className="text-slate-500 font-medium text-lg">
              Four steps from your couch to your delivery.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {/* Step 1 */}
            <div
              className="card-hover bg-background-light rounded-3xl overflow-hidden border border-transparent hover:border-primary/15 fade-up relative group"
              style={{ transitionDelay: "0s" }}
            >
              <div className="h-52 bg-gradient-to-br from-orange-50 to-amber-100/80 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-25"
                  style={{
                    backgroundImage:
                      "radial-gradient(#ff6933 1px,transparent 1px)",
                    backgroundSize: "18px 18px",
                  }}
                />
                {/* Phone frame with typing animation */}
                <div className="illus-zoom relative z-10 w-[88px] h-[148px] bg-white rounded-[18px] shadow-2xl border-2 border-slate-100/80 flex flex-col items-center justify-start gap-0 overflow-hidden">
                  {/* Phone header */}
                  <div className="w-full h-6 bg-slate-50 flex items-center justify-center border-b border-slate-100">
                    <div className="w-12 h-1.5 bg-slate-200 rounded-full" />
                  </div>
                  {/* Content lines */}
                  <div className="w-full p-3 flex flex-col gap-2 flex-1">
                    <div className="w-full h-2 bg-slate-100 rounded-full" />
                    <div className="w-4/5 h-2 bg-slate-100 rounded-full" />
                    <div className="w-full h-2 bg-slate-100 rounded-full" />
                    <div className="w-3/5 h-2 bg-primary/25 rounded-full" />
                    <div className="mt-1.5 w-full h-12 bg-primary rounded-xl flex items-center justify-center shadow-lg shadow-primary/30">
                      <span
                        className="material-symbols-outlined text-white text-2xl"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        edit_note
                      </span>
                    </div>
                  </div>
                </div>
                {/* Floating draw icon */}
                <div className="absolute top-5 right-7 bg-white rounded-full p-2 shadow-lg pin-1 border border-primary/10">
                  <span
                    className="material-symbols-outlined text-primary text-base"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    draw
                  </span>
                </div>
                {/* Step label */}
                <div className="absolute bottom-4 left-4 bg-primary text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                  Step 01
                </div>
              </div>
              <div className="p-6 relative">
                <div className="step-num">01</div>
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    edit_note
                  </span>
                </div>
                <h3 className="text-base font-black mb-2">
                  Describe Your Errand
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Tell us what needs to be picked up, dropped off, or delivered
                  — documents, groceries, packages, anything.
                </p>
              </div>
            </div>
            {/* Step 2 */}
            <div
              className="card-hover bg-background-light rounded-3xl overflow-hidden border border-transparent hover:border-primary/15 fade-up relative group"
              style={{ transitionDelay: ".08s" }}
            >
              <div className="h-52 bg-gradient-to-br from-orange-50 to-red-50 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#ff6933 1px,transparent 1px)",
                    backgroundSize: "18px 18px",
                  }}
                />
                <div className="illus-zoom relative z-10 flex flex-col gap-2 w-[148px]">
                  {/* Selected runner */}
                  <div className="active-runner-card px-3 py-2.5 shadow-md flex items-center gap-2.5">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                      <span
                        className="material-symbols-outlined text-white text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-black truncate">
                        Emeka A.
                      </div>
                      <div className="flex gap-0.5 items-center">
                        <span
                          className="material-symbols-outlined text-yellow-400 star-t1"
                          style={{
                            fontSize: 9,
                            fontVariationSettings: '"FILL" 1',
                          }}
                        >
                          star
                        </span>
                        <span
                          className="material-symbols-outlined text-yellow-400 star-t2"
                          style={{
                            fontSize: 9,
                            fontVariationSettings: '"FILL" 1',
                          }}
                        >
                          star
                        </span>
                        <span
                          className="material-symbols-outlined text-yellow-400 star-t3"
                          style={{
                            fontSize: 9,
                            fontVariationSettings: '"FILL" 1',
                          }}
                        >
                          star
                        </span>
                        <span className="text-[9px] text-slate-400 font-semibold ml-0.5">
                          4.9
                        </span>
                      </div>
                    </div>
                    <span
                      className="material-symbols-outlined text-green-500 text-base"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      check_circle
                    </span>
                  </div>
                  {/* Other runners */}
                  <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm flex items-center gap-2.5 opacity-55 border border-slate-100">
                    <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span
                        className="material-symbols-outlined text-slate-400 text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                    <div>
                      <div className="text-xs font-black text-slate-700">
                        Tunde K.
                      </div>
                      <div className="text-[9px] text-slate-400">
                        0.9 km · 4.7★
                      </div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2.5 shadow-sm flex items-center gap-2.5 opacity-35 border border-slate-100">
                    <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span
                        className="material-symbols-outlined text-slate-400 text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                    <div>
                      <div className="text-xs font-black text-slate-700">
                        Amara O.
                      </div>
                      <div className="text-[9px] text-slate-400">
                        1.2 km · 4.8★
                      </div>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 bg-primary text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                  Step 02
                </div>
              </div>
              <div className="p-6 relative">
                <div className="step-num">02</div>
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    how_to_reg
                  </span>
                </div>
                <h3 className="text-base font-black mb-2">Pick Your Runner</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Browse verified runners near you — see their ratings,
                  distance, specialties, and pricing. You decide who goes.
                </p>
              </div>
            </div>
            {/* Step 3 */}
            <div
              className="card-hover bg-background-light rounded-3xl overflow-hidden border border-transparent hover:border-primary/15 fade-up relative group"
              style={{ transitionDelay: ".16s" }}
            >
              <div className="h-52 bg-gradient-to-br from-green-50 to-emerald-100/70 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#22c55e 1px,transparent 1px)",
                    backgroundSize: "18px 18px",
                  }}
                />
                <div className="illus-zoom relative z-10 flex flex-col items-center gap-3">
                  {/* Card */}
                  <div className="w-[136px] bg-gradient-to-br from-slate-800 to-slate-950 rounded-2xl p-3.5 shadow-2xl">
                    <div className="flex justify-between items-start mb-3.5">
                      <div className="w-9 h-6 bg-yellow-400/85 rounded-md shadow-sm" />
                      <div className="w-7 h-7 bg-white/12 rounded-lg flex items-center justify-center backdrop-blur-sm">
                        <span
                          className="material-symbols-outlined text-white text-xs"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          lock
                        </span>
                      </div>
                    </div>
                    <div className="text-white text-[11px] font-mono font-bold tracking-widest opacity-90">
                      •••• •••• 4731
                    </div>
                  </div>
                  {/* Escrow badge */}
                  <div className="flex items-center gap-2 bg-white rounded-xl px-3.5 py-2 shadow-lg border border-green-100">
                    <span
                      className="material-symbols-outlined text-green-500 text-lg"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      check_circle
                    </span>
                    <span className="text-xs font-black text-slate-800">
                      Escrow Protected
                    </span>
                  </div>
                </div>
                {/* Bouncing coins */}
                <div className="absolute top-5 right-6 coin-1">
                  <span
                    className="material-symbols-outlined text-yellow-500 text-2xl"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    monetization_on
                  </span>
                </div>
                <div className="absolute top-12 right-12 coin-2">
                  <span
                    className="material-symbols-outlined text-yellow-400 text-base"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    monetization_on
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 bg-primary text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                  Step 03
                </div>
              </div>
              <div className="p-6 relative">
                <div className="step-num">03</div>
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    credit_card
                  </span>
                </div>
                <h3 className="text-base font-black mb-2">Pay Securely</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Pay via card, bank transfer, or USSD. Funds are held in escrow
                  and only released once your errand is confirmed done.
                </p>
              </div>
            </div>
            {/* Step 4 */}
            <div
              className="card-hover bg-background-light rounded-3xl overflow-hidden border border-transparent hover:border-primary/15 fade-up relative group"
              style={{ transitionDelay: ".24s" }}
            >
              <div className="h-52 bg-gradient-to-br from-blue-50 to-cyan-100/70 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#3b82f6 1px,transparent 1px)",
                    backgroundSize: "18px 18px",
                  }}
                />
                {/* Mini live map */}
                <div className="illus-zoom relative z-10 w-[148px] h-[112px] rounded-2xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage:
                        "linear-gradient(rgba(59,130,246,.09) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,.09) 1px,transparent 1px)",
                      backgroundSize: "22px 22px",
                    }}
                  />
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 148 112"
                    fill="none"
                  >
                    <rect
                      x={0}
                      y={48}
                      width={148}
                      height={14}
                      rx={3}
                      fill="#e2ddd9"
                      opacity="0.8"
                    />
                    <rect
                      x={66}
                      y={0}
                      width={14}
                      height={112}
                      rx={3}
                      fill="#e2ddd9"
                      opacity="0.8"
                    />
                    <line
                      x1={0}
                      y1={55}
                      x2={148}
                      y2={55}
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="10 6"
                      className="road-dash"
                    />
                    <line
                      x1={73}
                      y1={0}
                      x2={73}
                      y2={112}
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="10 6"
                      className="road-dash"
                    />
                  </svg>
                  {/* Runner on road */}
                  <div className="absolute top-[42%] left-[16%] z-10 runner-on-map">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
                      <span
                        className="material-symbols-outlined text-white text-xs"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                  </div>
                  {/* Dest */}
                  <div className="absolute top-[14%] right-[10%] pin-2">
                    <span
                      className="material-symbols-outlined text-primary text-lg"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      location_on
                    </span>
                  </div>
                  {/* User dot */}
                  <div className="absolute bottom-[18%] right-[28%]">
                    <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md relative">
                      <div className="absolute inset-0 rounded-full bg-blue-400 live-ping" />
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 bg-primary text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider shadow-lg">
                  Step 04
                </div>
              </div>
              <div className="p-6 relative">
                <div className="step-num">04</div>
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    distance
                  </span>
                </div>
                <h3 className="text-base font-black mb-2">
                  Track &amp; Receive
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Watch your runner live on the map and get alerts at every
                  milestone until your item arrives safely at your door.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════ TRUST & MAP ═══════════════════ */}
      <section className="py-28 px-6 lg:px-20 overflow-hidden section-gradient">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="fade-up">
            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase mb-6">
              <span className="material-symbols-outlined text-sm">radar</span>
              Live Coverage
            </div>
            <h2 className="text-4xl font-black text-slate-900 mb-6 leading-tight">
              Reliability you can
              <br />
              <span className="text-primary italic skew-heading">
                see on the map.
              </span>
            </h2>
            <p className="text-slate-500 text-[1.05rem] mb-10 font-medium leading-relaxed">
              Every active runner shows up on your live map. We match you to the
              closest verified runner so your errand is handled as fast as
              humanly possible.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-primary/6 group hover:border-primary/20 hover:shadow-md transition-all stat-card">
                <div
                  className="text-[2.2rem] font-black text-primary mb-1 leading-none"
                  data-target={8}
                  data-suffix=" min"
                >
                  —
                </div>
                <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                  Avg. Pickup Time
                </div>
              </div>
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-primary/6 group hover:border-primary/20 hover:shadow-md transition-all stat-card">
                <div
                  className="text-[2.2rem] font-black text-primary mb-1 leading-none"
                  data-target={300}
                  data-suffix="+"
                >
                  —
                </div>
                <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                  Active Runners Today
                </div>
              </div>
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-primary/6 group hover:border-primary/20 hover:shadow-md transition-all stat-card">
                <div
                  className="text-[2.2rem] font-black text-primary mb-1 leading-none"
                  data-target={99}
                  data-suffix="%"
                >
                  —
                </div>
                <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                  Successful Deliveries
                </div>
              </div>
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-primary/6 group hover:border-primary/20 hover:shadow-md transition-all stat-card">
                <div className="text-[2.2rem] font-black text-primary mb-1 leading-none">
                  ₦0
                </div>
                <div className="text-xs font-black text-slate-500 uppercase tracking-wider">
                  First Errand Fee
                </div>
              </div>
            </div>
          </div>
          {/* Large map illustration */}
          <div className="relative fade-up" style={{ transitionDelay: ".15s" }}>
            <div className="rounded-[2rem] overflow-hidden aspect-video shadow-2xl relative border-8 border-white bg-slate-50 ring-1 ring-slate-200/80">
              {/* Map grid */}
              <div className="absolute inset-0 map-grid" />
              {/* Road SVG */}
              <svg
                className="absolute inset-0 w-full h-full"
                viewBox="0 0 640 360"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMidYMid slice"
              >
                <rect
                  x={0}
                  y={158}
                  width={640}
                  height={30}
                  rx={5}
                  fill="#e2ddd9"
                />
                <rect
                  x={288}
                  y={0}
                  width={30}
                  height={360}
                  rx={5}
                  fill="#e2ddd9"
                />
                <line
                  x1={0}
                  y1={173}
                  x2={640}
                  y2={173}
                  stroke="white"
                  strokeWidth="2.5"
                  strokeDasharray="22 14"
                  className="road-dash"
                />
                <line
                  x1={303}
                  y1={0}
                  x2={303}
                  y2={360}
                  stroke="white"
                  strokeWidth="2.5"
                  strokeDasharray="22 14"
                  className="road-dash"
                />
                {/* Blocks */}
                <rect
                  x={18}
                  y={18}
                  width={252}
                  height={122}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.55"
                />
                <rect
                  x={338}
                  y={18}
                  width={284}
                  height={122}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.5"
                />
                <rect
                  x={18}
                  y={208}
                  width={252}
                  height={134}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.55"
                />
                <rect
                  x={338}
                  y={208}
                  width={284}
                  height={134}
                  rx={8}
                  fill="#e8e4df"
                  opacity="0.5"
                />
                {/* Buildings */}
                <rect
                  x={46}
                  y={36}
                  width={22}
                  height={50}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.65"
                />
                <rect
                  x={78}
                  y={50}
                  width={17}
                  height={28}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.5"
                />
                <rect
                  x={110}
                  y={30}
                  width={26}
                  height={68}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.55"
                />
                <rect
                  x={370}
                  y={34}
                  width={26}
                  height={62}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.65"
                />
                <rect
                  x={408}
                  y={48}
                  width={20}
                  height={44}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.5"
                />
                <rect
                  x={450}
                  y={28}
                  width={18}
                  height={76}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.55"
                />
                <rect
                  x={46}
                  y={228}
                  width={22}
                  height={42}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.55"
                />
                <rect
                  x={370}
                  y={224}
                  width={26}
                  height={56}
                  rx={3}
                  fill="#cbc5bf"
                  opacity="0.55"
                />
              </svg>
              {/* Moving runner */}
              <div className="absolute z-10 runner-on-map">
                <div
                  className="w-9 h-9 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center shadow-xl border-3 border-white"
                  style={{ borderWidth: 3 }}
                >
                  <span
                    className="material-symbols-outlined text-white text-sm"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    directions_run
                  </span>
                </div>
              </div>
              {/* Static pins */}
              <div className="absolute top-[22%] left-[36%] bg-primary text-white p-2 rounded-xl shadow-lg pin-1 z-10">
                <span
                  className="material-symbols-outlined text-sm"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  person_pin_circle
                </span>
              </div>
              <div className="absolute bottom-[30%] right-[22%] bg-primary text-white p-2 rounded-xl shadow-lg pin-3 z-10">
                <span
                  className="material-symbols-outlined text-sm"
                  style={{ fontVariationSettings: '"FILL" 1' }}
                >
                  person_pin_circle
                </span>
              </div>
              {/* User dot */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <div className="w-5 h-5 bg-blue-600 rounded-full border-[3px] border-white shadow-xl relative">
                  <div className="absolute inset-0 bg-blue-400 rounded-full live-ping" />
                </div>
              </div>
              {/* Overlays */}
              <div className="absolute top-3 right-3 bg-slate-900/90 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-2 z-20">
                <div className="w-2 h-2 bg-green-400 rounded-full status-blink" />
                42 Runners Active Nearby
              </div>
              <div className="absolute bottom-3 left-3 bg-white rounded-2xl p-3 shadow-xl flex items-center gap-3 z-20 border border-slate-100/80">
                <div className="bg-primary/10 p-2 rounded-xl">
                  <span
                    className="material-symbols-outlined text-primary text-base"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    directions_run
                  </span>
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 font-semibold">
                    Emeka A. · 0.4 km away
                  </div>
                  <div className="text-sm font-black text-slate-900">
                    Arriving in <span className="text-primary">8 min</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Floating decorative badge */}
            <div className="badge-pop absolute -bottom-5 -right-5 bg-primary text-white rounded-2xl px-5 py-3 shadow-2xl shadow-primary/30 flex items-center gap-2.5 z-30">
              <span
                className="material-symbols-outlined text-2xl"
                style={{ fontVariationSettings: '"FILL" 1' }}
              >
                verified
              </span>
              <div>
                <div className="text-[10px] font-bold opacity-75 uppercase tracking-wider">
                  All runners
                </div>
                <div className="text-sm font-black">Verified &amp; Insured</div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════ FEATURES ═══════════════════ */}
      <section id="features" className="py-28 px-6 lg:px-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16 fade-up">
            <div className="inline-flex items-center gap-2 bg-primary/8 text-primary px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase mb-5">
              <span className="material-symbols-outlined text-sm">
                auto_awesome
              </span>
              Platform Features
            </div>
            <h2 className="text-4xl font-black text-slate-900 skew-heading mb-4">
              Built for real life
            </h2>
            <p className="text-slate-500 font-medium text-lg max-w-lg">
              Everything you need to send errands with complete confidence.
            </p>
          </div>
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5 fade-up">
            {/* Live Tracking — wide card */}
            <div className="card-hover md:col-span-2 bg-background-light rounded-3xl overflow-hidden border border-primary/5">
              <div className="h-56 bg-gradient-to-r from-primary/8 via-orange-50 to-amber-50/80 relative flex items-center overflow-hidden px-6">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#ff6933 1px,transparent 1px)",
                    backgroundSize: "22px 22px",
                  }}
                />
                {/* Progress timeline */}
                <div className="relative z-10 flex flex-col gap-0 flex-shrink-0 mr-8">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <span
                        className="material-symbols-outlined text-white text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        check
                      </span>
                    </div>
                    <span className="text-xs font-black text-slate-700">
                      Accepted
                    </span>
                  </div>
                  <div className="ml-4 w-0.5 h-5 bg-primary/30 my-1" />
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <span
                        className="material-symbols-outlined text-white text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        check
                      </span>
                    </div>
                    <span className="text-xs font-black text-slate-700">
                      Picked Up
                    </span>
                  </div>
                  <div className="ml-4 w-0.5 h-5 bg-primary/30 my-1" />
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center shadow-xl flex-shrink-0 tracker-active">
                      <span
                        className="material-symbols-outlined text-white text-sm runner-anim"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                    <span className="text-xs font-black text-primary">
                      En Route
                    </span>
                  </div>
                  <div className="ml-4 w-0.5 h-5 bg-slate-200 my-1" />
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span
                        className="material-symbols-outlined text-slate-400 text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        home
                      </span>
                    </div>
                    <span className="text-xs font-medium text-slate-400">
                      Delivered
                    </span>
                  </div>
                </div>
                {/* Mini map */}
                <div className="relative z-10 flex-1 h-40 rounded-2xl bg-white shadow-xl border border-slate-100 overflow-hidden">
                  <div className="absolute inset-0 map-grid opacity-40" />
                  <svg
                    className="absolute inset-0 w-full h-full"
                    viewBox="0 0 200 150"
                    fill="none"
                  >
                    <rect
                      x={0}
                      y={66}
                      width={200}
                      height={16}
                      rx={3}
                      fill="#e2ddd9"
                      opacity="0.8"
                    />
                    <rect
                      x={90}
                      y={0}
                      width={16}
                      height={150}
                      rx={3}
                      fill="#e2ddd9"
                      opacity="0.8"
                    />
                    <line
                      x1={0}
                      y1={74}
                      x2={200}
                      y2={74}
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="12 8"
                      className="road-dash"
                    />
                    <line
                      x1={98}
                      y1={0}
                      x2={98}
                      y2={150}
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="12 8"
                      className="road-dash"
                    />
                  </svg>
                  {/* Moving runner on this map */}
                  <div className="absolute top-[44%] left-[16%] z-10 pin-1">
                    <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center shadow-md border-2 border-white">
                      <span
                        className="material-symbols-outlined text-white text-xs"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        directions_run
                      </span>
                    </div>
                  </div>
                  {/* Dest */}
                  <div className="absolute top-[16%] right-[10%] pin-2">
                    <span
                      className="material-symbols-outlined text-primary text-xl"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      location_on
                    </span>
                  </div>
                  {/* User */}
                  <div className="absolute bottom-[22%] right-[28%]">
                    <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow relative">
                      <div className="absolute inset-0 rounded-full bg-blue-400 live-ping" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Progress bar */}
              <div className="px-6 pt-0 pb-1 -mt-1">
                <div className="bg-white/90 backdrop-blur-sm px-4 py-3 rounded-2xl border border-primary/8 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                      Errand Progress
                    </span>
                    <span className="text-[11px] font-black text-primary flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full status-blink" />
                      En Route
                    </span>
                  </div>
                  <div className="relative h-2 bg-slate-100 rounded-full mb-3 overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full rounded-full progress-animated"
                      style={{
                        background: "linear-gradient(90deg, #ff6933, #ff9a5c)",
                      }}
                    />
                  </div>
                  <div className="grid grid-cols-4 text-center gap-1">
                    <div>
                      <div className="w-2.5 h-2.5 bg-primary rounded-full mx-auto mb-1 shadow-sm" />
                      <span className="text-[9px] text-slate-400 font-semibold">
                        Accepted
                      </span>
                    </div>
                    <div>
                      <div className="w-2.5 h-2.5 bg-primary rounded-full mx-auto mb-1 shadow-sm" />
                      <span className="text-[9px] text-slate-400 font-semibold">
                        Picked Up
                      </span>
                    </div>
                    <div>
                      <div className="w-3 h-3 bg-primary rounded-full mx-auto mb-1 tracker-active shadow-sm -mt-0.5" />
                      <span className="text-[9px] text-primary font-black">
                        En Route
                      </span>
                    </div>
                    <div>
                      <div className="w-2.5 h-2.5 bg-slate-200 rounded-full mx-auto mb-1" />
                      <span className="text-[9px] text-slate-300 font-semibold">
                        Delivered
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6 pt-4">
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-3">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    location_on
                  </span>
                </div>
                <h3 className="text-xl font-black mb-1.5">
                  Live Runner Tracking
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Watch your runner move on a live map, updated every few
                  seconds. Know exactly when they've picked up your item and how
                  far away they are — in real time.
                </p>
              </div>
            </div>
            {/* Escrow Payments */}
            <div className="card-hover bg-background-light rounded-3xl overflow-hidden border border-primary/5">
              <div className="h-56 bg-gradient-to-b from-green-50 to-emerald-100/50 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#22c55e 1px,transparent 1px)",
                    backgroundSize: "18px 18px",
                  }}
                />
                <div className="relative z-10 flex flex-col items-center gap-3.5">
                  <div className="relative">
                    <div className="w-20 h-20 bg-white rounded-2xl shadow-2xl flex items-center justify-center border-2 border-green-100 shield-glow">
                      <span
                        className="material-symbols-outlined text-green-500"
                        style={{
                          fontSize: "3rem",
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        lock
                      </span>
                    </div>
                    <div className="absolute -top-2.5 -right-2.5 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                      <span
                        className="material-symbols-outlined text-white text-sm"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        check
                      </span>
                    </div>
                  </div>
                  {/* Escrow bar fill */}
                  <div className="w-36 bg-white rounded-xl p-2.5 shadow-lg border border-green-100">
                    <div className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">
                      Payment secured
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full escrow-bar rounded-full" />
                    </div>
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-xs font-black text-slate-800">
                        ₦1,500
                      </span>
                      <span className="text-[9px] font-black text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                        Safe ✓
                      </span>
                    </div>
                  </div>
                </div>
                {/* coin */}
                <div className="absolute top-5 right-5 coin-1">
                  <span
                    className="material-symbols-outlined text-yellow-500 text-2xl"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    monetization_on
                  </span>
                </div>
                <div className="absolute top-12 right-12 coin-2">
                  <span
                    className="material-symbols-outlined text-yellow-400 text-base"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    monetization_on
                  </span>
                </div>
              </div>
              <div className="p-6 flex flex-col gap-3">
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    lock
                  </span>
                </div>
                <h3 className="text-xl font-black">Escrow Payments</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Funds are held safely and only released when you confirm the
                  job is done.
                </p>
                <div className="bg-white rounded-2xl p-3 flex items-center gap-3 border border-green-100/80 mt-auto shadow-sm">
                  <div className="bg-green-100 p-2 rounded-xl flex-shrink-0">
                    <span
                      className="material-symbols-outlined text-green-600 text-base"
                      style={{ fontVariationSettings: '"FILL" 1' }}
                    >
                      check_circle
                    </span>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 font-semibold">
                      Held in escrow
                    </div>
                    <div className="text-sm font-black text-slate-900">
                      ₦1,500{" "}
                      <span className="text-xs font-bold text-green-600">
                        Protected
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Row 2 */}
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-5 fade-up"
            style={{ transitionDelay: ".12s" }}
          >
            {/* Verified & Insured */}
            <div className="card-hover bg-background-light rounded-3xl overflow-hidden border border-primary/5">
              <div className="h-44 bg-gradient-to-br from-primary/8 to-amber-50 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#ff6933 1px,transparent 1px)",
                    backgroundSize: "16px 16px",
                  }}
                />
                <div className="illus-zoom relative z-10 flex flex-col items-center gap-2">
                  {/* ID badge */}
                  <div className="w-[120px] bg-white rounded-2xl shadow-2xl border border-slate-100 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center shadow-sm">
                        <span
                          className="material-symbols-outlined text-white text-base"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          directions_run
                        </span>
                      </div>
                      <div>
                        <div className="text-xs font-black text-slate-800">
                          Emeka A.
                        </div>
                        <div className="text-[9px] text-slate-400">
                          Runner #4821
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-2">
                      <span
                        className="material-symbols-outlined text-yellow-400 star-t1"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        star
                      </span>
                      <span
                        className="material-symbols-outlined text-yellow-400 star-t2"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        star
                      </span>
                      <span
                        className="material-symbols-outlined text-yellow-400 star-t3"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        star
                      </span>
                      <span
                        className="material-symbols-outlined text-yellow-400 star-t4"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        star
                      </span>
                      <span
                        className="material-symbols-outlined text-yellow-400 star-t5"
                        style={{
                          fontSize: 11,
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        star
                      </span>
                    </div>
                    <div className="flex items-center gap-1 bg-green-50 rounded-full px-2 py-1">
                      <span
                        className="material-symbols-outlined text-green-500 text-xs"
                        style={{ fontVariationSettings: '"FILL" 1' }}
                      >
                        verified
                      </span>
                      <span className="text-[9px] font-black text-green-700">
                        Verified &amp; Insured
                      </span>
                    </div>
                  </div>
                </div>
                {/* floating stars */}
                <div className="absolute bottom-3 left-3 flex gap-1 items-center">
                  <span
                    className="material-symbols-outlined text-primary star-t1"
                    style={{ fontSize: 13, fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span
                    className="material-symbols-outlined text-primary star-t2"
                    style={{ fontSize: 13, fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span
                    className="material-symbols-outlined text-primary star-t3"
                    style={{ fontSize: 13, fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span
                    className="material-symbols-outlined text-primary star-t4"
                    style={{ fontSize: 13, fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span
                    className="material-symbols-outlined text-primary star-t5"
                    style={{ fontSize: 13, fontVariationSettings: '"FILL" 1' }}
                  >
                    star
                  </span>
                  <span className="text-primary text-xs font-black ml-1">
                    5.0
                  </span>
                </div>
              </div>
              <div className="p-6">
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-3">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    verified_user
                  </span>
                </div>
                <h3 className="text-lg font-black mb-1.5">
                  Verified &amp; Insured
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Every runner passes background checks and is covered by our
                  delivery insurance on every trip.
                </p>
              </div>
            </div>
            {/* In-App Chat — sequential bubble animation */}
            <div className="card-hover bg-background-light rounded-3xl overflow-hidden border border-primary/5">
              <div className="h-44 bg-gradient-to-br from-blue-50 to-indigo-50/80 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#6366f1 1px,transparent 1px)",
                    backgroundSize: "16px 16px",
                  }}
                />
                <div className="illus-zoom relative z-10 flex flex-col gap-2 w-44">
                  <div className="chat-1 self-end bg-gradient-to-br from-primary to-primary-deep text-white text-xs font-semibold px-3.5 py-2 rounded-2xl rounded-br-sm shadow-lg max-w-[82%]">
                    Are you nearby?
                  </div>
                  <div className="chat-2 self-start bg-white text-slate-700 text-xs font-semibold px-3.5 py-2 rounded-2xl rounded-bl-sm shadow-md max-w-[82%] border border-slate-100">
                    Yes! 3 min away 🏃
                  </div>
                  <div className="chat-3 self-end bg-gradient-to-br from-primary to-primary-deep text-white text-xs font-semibold px-3.5 py-2 rounded-2xl rounded-br-sm shadow-lg max-w-[82%]">
                    Great, see you soon!
                  </div>
                  {/* Typing indicator */}
                  <div className="self-start bg-white px-3.5 py-2.5 rounded-2xl rounded-bl-sm shadow-md flex items-center gap-1.5 border border-slate-100">
                    <div className="w-2 h-2 bg-slate-400 rounded-full dot-1" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full dot-2" />
                    <div className="w-2 h-2 bg-slate-400 rounded-full dot-3" />
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-3">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    chat
                  </span>
                </div>
                <h3 className="text-lg font-black mb-1.5">In-App Chat</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Message your runner directly inside the app. Your personal
                  number is never shared.
                </p>
              </div>
            </div>
            {/* Instant Matching */}
            <div className="card-hover bg-background-light rounded-3xl overflow-hidden border border-primary/5">
              <div className="h-44 bg-gradient-to-br from-yellow-50 to-amber-100/60 relative flex items-center justify-center overflow-hidden">
                <div
                  className="absolute inset-0 opacity-15"
                  style={{
                    backgroundImage:
                      "radial-gradient(#f59e0b 1px,transparent 1px)",
                    backgroundSize: "16px 16px",
                  }}
                />
                <div className="illus-zoom relative z-10 flex flex-col items-center gap-3">
                  <div className="w-16 h-16 bg-white rounded-2xl shadow-2xl flex items-center justify-center border border-yellow-100">
                    <span
                      className="material-symbols-outlined text-primary bolt-flash"
                      style={{
                        fontSize: "2.8rem",
                        fontVariationSettings: '"FILL" 1',
                      }}
                    >
                      bolt
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <div className="flex -space-x-2.5">
                      <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-deep rounded-xl flex items-center justify-center border-2 border-white shadow-md">
                        <span
                          className="material-symbols-outlined text-white text-sm"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          directions_run
                        </span>
                      </div>
                      <div className="w-9 h-9 bg-primary/60 rounded-xl flex items-center justify-center border-2 border-white shadow-md">
                        <span
                          className="material-symbols-outlined text-white text-sm"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          directions_run
                        </span>
                      </div>
                      <div className="w-9 h-9 bg-primary/35 rounded-xl flex items-center justify-center border-2 border-white shadow-md">
                        <span
                          className="material-symbols-outlined text-white text-sm"
                          style={{ fontVariationSettings: '"FILL" 1' }}
                        >
                          directions_run
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-black text-slate-700 bg-white rounded-full px-2.5 py-1 shadow-sm">
                      Matched! ✓
                    </span>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary mb-3">
                  <span className="material-symbols-outlined text-[1.3rem]">
                    bolt
                  </span>
                </div>
                <h3 className="text-lg font-black mb-1.5">Instant Matching</h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Our algorithm surfaces the closest, highest-rated available
                  runner for your job in seconds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════ BECOME A RUNNER ═══════════════════ */}
      <section id="become" className="py-28 px-6 lg:px-20 section-gradient">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Runner earnings panel */}
          <div className="relative rounded-[2.5rem] overflow-hidden h-[500px] shadow-2xl fade-up order-last lg:order-first bg-background-dark">
            <div className="absolute inset-0 hero-pattern-dark opacity-[0.1] pointer-events-none" />
            {/* Ambient glow */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
            {/* City silhouette */}
            <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center gap-0.5 opacity-[0.08]">
              <div className="w-10 h-40 bg-white rounded-t" />
              <div className="w-7  h-56 bg-white rounded-t" />
              <div className="w-14 h-32 bg-white rounded-t" />
              <div className="w-5  h-48 bg-white rounded-t" />
              <div className="w-12 h-36 bg-white rounded-t" />
              <div className="w-7  h-60 bg-white rounded-t" />
              <div className="w-10 h-44 bg-white rounded-t" />
              <div className="w-5  h-52 bg-white rounded-t" />
              <div className="w-14 h-38 bg-white rounded-t" />
            </div>
            {/* Centre runner with orbits */}
            <div
              className="absolute inset-0 flex items-center justify-center z-10"
              style={{ marginTop: "-40px" }}
            >
              <div className="relative">
                {/* Concentric rings */}
                <div
                  className="absolute inset-0 m-auto rounded-full bg-primary/10 ring-pulse-a"
                  style={{
                    width: 140,
                    height: 140,
                    top: "-18px",
                    left: "-18px",
                  }}
                />
                <div
                  className="absolute inset-0 m-auto rounded-full bg-primary/08 ring-pulse-b"
                  style={{
                    width: 140,
                    height: 140,
                    top: "-18px",
                    left: "-18px",
                  }}
                />
                {/* Runner circle */}
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary-deep flex items-center justify-center shadow-2xl shadow-primary/50 relative z-10">
                  <span
                    className="material-symbols-outlined text-white runner-anim"
                    style={{
                      fontSize: "2.4rem",
                      fontVariationSettings: '"FILL" 1',
                    }}
                  >
                    directions_run
                  </span>
                </div>
                {/* Orbit icons */}
                <div
                  className="absolute inset-0 flex items-center justify-center"
                  style={{ top: "-12px", left: "-12px" }}
                >
                  <div className="relative w-0 h-0">
                    <div
                      className="orbit-1 absolute w-10 h-10 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/60"
                      style={{ top: "-10px", left: "-10px" }}
                    >
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{
                          fontSize: "1.1rem",
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        inventory_2
                      </span>
                    </div>
                    <div
                      className="orbit-2 absolute w-10 h-10 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/60"
                      style={{ top: "-10px", left: "-10px" }}
                    >
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{
                          fontSize: "1.1rem",
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        local_pharmacy
                      </span>
                    </div>
                    <div
                      className="orbit-3 absolute w-10 h-10 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-orange-100/60"
                      style={{ top: "-10px", left: "-10px" }}
                    >
                      <span
                        className="material-symbols-outlined text-primary"
                        style={{
                          fontSize: "1.1rem",
                          fontVariationSettings: '"FILL" 1',
                        }}
                      >
                        shopping_bag
                      </span>
                    </div>
                  </div>
                </div>
                {/* Floating coins */}
                <div className="absolute -top-10 -right-5 coin-1">
                  <span
                    className="material-symbols-outlined text-yellow-400"
                    style={{
                      fontSize: "1.8rem",
                      fontVariationSettings: '"FILL" 1',
                    }}
                  >
                    monetization_on
                  </span>
                </div>
                <div className="absolute -top-3 -left-12 coin-2">
                  <span
                    className="material-symbols-outlined text-yellow-300"
                    style={{
                      fontSize: "1.4rem",
                      fontVariationSettings: '"FILL" 1',
                    }}
                  >
                    monetization_on
                  </span>
                </div>
                <div className="absolute top-8 -right-12 coin-3">
                  <span
                    className="material-symbols-outlined text-yellow-500"
                    style={{
                      fontSize: "1.3rem",
                      fontVariationSettings: '"FILL" 1',
                    }}
                  >
                    monetization_on
                  </span>
                </div>
              </div>
            </div>
            {/* Earnings card */}
            <div className="absolute bottom-8 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-2xl z-20">
              <div className="flex items-center justify-between mb-4">
                <div className="earn-appear">
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">
                    Today's earnings
                  </div>
                  <div className="text-2xl font-black text-slate-900 flex items-baseline gap-2">
                    ₦6,400
                    <span className="text-green-500 text-sm font-bold flex items-center gap-0.5">
                      <svg
                        width={10}
                        height={12}
                        viewBox="0 0 10 12"
                        fill="none"
                      >
                        <path d="M5 1l4 4H1l4-4z" fill="currentColor" />
                      </svg>
                      +12%
                    </span>
                  </div>
                </div>
                <div className="bg-primary/10 p-3 rounded-2xl">
                  <span
                    className="material-symbols-outlined text-primary text-2xl"
                    style={{ fontVariationSettings: '"FILL" 1' }}
                  >
                    payments
                  </span>
                </div>
              </div>
              {/* Mini bar chart */}
              <div className="flex items-end gap-1 h-8 mb-3">
                <div
                  className="flex-1 rounded-t bg-primary/20"
                  style={{ height: "40%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary/35"
                  style={{ height: "55%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary/50"
                  style={{ height: "65%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary/70"
                  style={{ height: "80%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary"
                  style={{ height: "100%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary/40"
                  style={{ height: "50%" }}
                />
                <div
                  className="flex-1 rounded-t bg-primary/25"
                  style={{ height: "30%" }}
                />
              </div>
              <div className="text-[10px] text-slate-400 font-semibold">
                8 errands completed · Last payout: 2h ago
              </div>
            </div>
          </div>
          <div className="fade-up" style={{ transitionDelay: ".12s" }}>
            <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase inline-block mb-6">
              For Runners
            </span>
            <h2 className="text-4xl font-black text-slate-900 mb-6 leading-tight">
              Earn on your own
              <br />
              <span className="text-primary italic skew-heading">
                schedule.
              </span>
            </h2>
            <p className="text-slate-500 text-[1.05rem] mb-8 font-medium leading-relaxed">
              Join hundreds of runners already earning with Errandi. Accept jobs
              near you, complete errands, get paid — all at your own pace with
              no minimum hours.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white p-4 rounded-2xl text-center border border-primary/6 shadow-sm hover:shadow-md hover:border-primary/20 transition-all">
                <div className="text-[1.6rem] font-black text-primary leading-none">
                  ₦5k+
                </div>
                <div className="text-[10px] font-black text-slate-500 mt-1.5 uppercase tracking-wider">
                  Avg. Daily Earnings
                </div>
              </div>
              <div className="bg-white p-4 rounded-2xl text-center border border-primary/6 shadow-sm hover:shadow-md hover:border-primary/20 transition-all">
                <div className="text-[1.6rem] font-black text-primary leading-none">
                  Flex
                </div>
                <div className="text-[10px] font-black text-slate-500 mt-1.5 uppercase tracking-wider">
                  Work Your Hours
                </div>
              </div>
              <div className="bg-white p-4 rounded-2xl text-center border border-primary/6 shadow-sm hover:shadow-md hover:border-primary/20 transition-all">
                <div className="text-[1.6rem] font-black text-primary leading-none">
                  Fast
                </div>
                <div className="text-[10px] font-black text-slate-500 mt-1.5 uppercase tracking-wider">
                  Same-Day Payouts
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2.5 mb-8">
              <div className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-primary/6 hover:border-primary/20 hover:shadow-md transition-all group cursor-pointer">
                <div className="bg-primary/10 p-2.5 rounded-xl flex-shrink-0 group-hover:bg-primary transition-colors">
                  <span className="material-symbols-outlined text-primary text-xl group-hover:text-white transition-colors">
                    badge
                  </span>
                </div>
                <div>
                  <div className="font-black text-sm">
                    Complete Verification
                  </div>
                  <div className="text-xs text-slate-500">
                    Quick ID check and background screening.
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300 ml-auto text-sm group-hover:text-primary group-hover:translate-x-1 transition-all">
                  arrow_forward_ios
                </span>
              </div>
              <div className="bg-white rounded-2xl p-4 flex items-center gap-4 border border-primary/6 hover:border-primary/20 hover:shadow-md transition-all group cursor-pointer">
                <div className="bg-primary/10 p-2.5 rounded-xl flex-shrink-0 group-hover:bg-primary transition-colors">
                  <span className="material-symbols-outlined text-primary text-xl group-hover:text-white transition-colors">
                    payments
                  </span>
                </div>
                <div>
                  <div className="font-black text-sm">Start Earning Today</div>
                  <div className="text-xs text-slate-500">
                    Accept jobs near you and get paid same day.
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300 ml-auto text-sm group-hover:text-primary group-hover:translate-x-1 transition-all">
                  arrow_forward_ios
                </span>
              </div>
            </div>
            <button className="bg-primary text-white font-extrabold px-10 py-4 rounded-2xl hover:shadow-2xl hover:shadow-primary/25 hover:scale-[1.03] transition-all active:scale-95 relative overflow-hidden group">
              <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
              <span className="relative z-10">Apply as a Runner →</span>
            </button>
          </div>
        </div>
      </section>
      {/* ═══════════════════ FLEET ═══════════════════ */}
      <section id="fleet" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-primary/5 blur-3xl" />
        </div>
        <div className="max-w-6xl mx-auto px-6 md:px-10 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-12">
            {/* Text */}
            <div className="flex-1 fade-up">
              <div className="inline-flex items-center gap-2 bg-primary/20 text-primary px-3 py-1.5 rounded-full text-xs font-bold mb-6 uppercase tracking-widest">
                <span className="material-symbols-outlined text-[14px]" style={{ fontVariationSettings: '"FILL" 1' }}>local_shipping</span>
                For Businesses
              </div>
              <h2 className="text-white text-3xl md:text-4xl font-black tracking-tight leading-tight mb-4">
                Register your delivery fleet
              </h2>
              <p className="text-slate-400 text-base leading-relaxed mb-8 max-w-md">
                Own a logistics company or manage a fleet of delivery vehicles? Register your organisation on Errandi and connect your drivers to thousands of errands on the platform.
              </p>
              <ul className="flex flex-col gap-3 mb-10">
                {[
                  { icon: "inventory_2",     text: "Manage all vehicles in one place" },
                  { icon: "group",           text: "Assign drivers to deliveries" },
                  { icon: "trending_up",     text: "Track fleet performance & earnings" },
                  { icon: "support_agent",   text: "Dedicated business support" },
                ].map(({ icon, text }) => (
                  <li key={text} className="flex items-center gap-3 text-slate-300 text-sm">
                    <div className="bg-primary/20 p-1.5 rounded-lg flex-shrink-0">
                      <span className="material-symbols-outlined text-primary text-[16px]" style={{ fontVariationSettings: '"FILL" 1' }}>{icon}</span>
                    </div>
                    {text}
                  </li>
                ))}
              </ul>
              <button className="bg-primary text-white font-extrabold px-10 py-4 rounded-2xl hover:shadow-2xl hover:shadow-primary/25 hover:scale-[1.03] transition-all active:scale-95 relative overflow-hidden group">
                <span className="absolute inset-0 bg-primary-deep scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 rounded-2xl" />
                <span className="relative z-10">Register your Fleet →</span>
              </button>
            </div>
            {/* Illustration cards */}
            <div className="flex-1 grid grid-cols-2 gap-4 fade-up" data-delay="0.15">
              {[
                { icon: "local_shipping", label: "Trucks",    count: "12", color: "bg-blue-500/10 text-blue-400" },
                { icon: "directions_car", label: "Cars",      count: "28", color: "bg-green-500/10 text-green-400" },
                { icon: "airport_shuttle",label: "Vans",      count: "7",  color: "bg-amber-500/10 text-amber-400" },
                { icon: "directions_bike",label: "Bikes",     count: "45", color: "bg-purple-500/10 text-purple-400" },
              ].map(({ icon, label, count, color }) => (
                <div key={label} className="bg-white/5 border border-white/10 rounded-2xl p-5 flex flex-col gap-3 backdrop-blur-sm hover:bg-white/10 transition-colors">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
                    <span className="material-symbols-outlined text-[22px]" style={{ fontVariationSettings: '"FILL" 1' }}>{icon}</span>
                  </div>
                  <p className="text-white text-2xl font-black">{count}</p>
                  <p className="text-slate-400 text-xs">{label} in fleet</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* ═══════════════════ CTA ═══════════════════ */}
      <HomeCtaSection />
      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <HomeFooterSection />
    </div>
  );
}
